import { SxProps } from '@mui/material'

import { LOCAL_STORAGE_LAST_SCROLL_POSITION } from '@utils/local-storage'

export interface ISaveScrollPosition {
  top: number
  path: string
  useOutlet?: boolean
}

type ScrollableElement = Window | HTMLElement

export const invisibleScrollSx: SxProps = {
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
}

export const scrollTo = ({
  top = 0,
  using,
}: {
  top?: number
  using?: Window | HTMLElement | null
}) => {
  setTimeout(() => {
    const element = using || window

    try {
      element.scrollTo({ top, behavior: 'smooth' })
    } catch {
      // Fallback to the legacy API for older browsers
      element.scrollTo(0, top)
    }
  }, 0)
}

export const getScrollableElement = (
  customElement?: HTMLElement
): ScrollableElement => {
  if (customElement) {
    return customElement
  }

  const outletWrapper = document.getElementById('outlet-wrapper')
  const hasScrollableContent =
    outletWrapper && outletWrapper.scrollHeight > outletWrapper.clientHeight

  return (hasScrollableContent ? outletWrapper : window) as HTMLElement
}

export const scrollToTop = () => {
  const scrollableElement = getScrollableElement()
  scrollTo({ top: 0, using: scrollableElement })
}

export const saveScrollPosition = ({
  useOutlet,
}: {
  useOutlet?: boolean
} = {}) => {
  const scrollPositionProps: ISaveScrollPosition = {
    top: window.scrollY,
    path: window.location.pathname,
    useOutlet,
  }

  if (useOutlet) {
    scrollPositionProps.top =
      document.querySelector('#outlet-wrapper')!.scrollTop
  }

  localStorage.setItem(
    LOCAL_STORAGE_LAST_SCROLL_POSITION,
    JSON.stringify(scrollPositionProps)
  )
}

const isWindow = (element: ScrollableElement): element is Window =>
  element === window

export const scrollToElement = ({
  targetId,
  ref,
  timeout = 0,
  topOffset = 0,
  usingCustomElement,
}: {
  targetId?: string
  ref?: React.RefObject<HTMLDivElement>
  timeout?: number
  topOffset?: number
  usingCustomElement?: HTMLElement
}) => {
  setTimeout(() => {
    if (targetId) {
      const element = document.getElementById(targetId)

      if (element) {
        const scrollableElement = getScrollableElement(usingCustomElement)
        const elementRect = element.getBoundingClientRect()

        const scrollTop = isWindow(scrollableElement)
          ? window.scrollY
          : scrollableElement.scrollTop

        const containerTop = isWindow(scrollableElement)
          ? 0
          : scrollableElement.getBoundingClientRect().top

        let offsetPosition =
          elementRect.top + scrollTop - containerTop - (topOffset || 0)

        if (usingCustomElement) {
          const elementStyle = window.getComputedStyle(usingCustomElement)
          const transformMatrix =
            elementStyle.transform.match(/matrix\((.*?)\)/)
          if (transformMatrix) {
            const values = transformMatrix[1].split(', ')
            const scaleX = parseFloat(values[0])
            offsetPosition = offsetPosition * (1 / scaleX)
          }
        }

        scrollTo({
          top: offsetPosition,
          using: scrollableElement,
        })
      }
    }

    if (ref) {
      scrollTo({ top: 0, using: ref.current })
    }
  }, timeout)
}

const exports = {
  scrollTo,
  scrollToTop,
  saveScrollPosition,
  scrollToElement,
  getScrollableElement,
}

export default exports
